import { useEffect, useRef, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classnames from 'classnames';
import { CSS_CLASSES } from './_constants';
import PropTypes from 'prop-types';
import { formatForID } from '../../../../utils/strings/StringUtil';

import classes from './DateSelect.module.scss';

const FORMAT_DATE = 'MM/DD/YYYY';
const YEAR_START = 1900;

const DateSelect = props => {
	const [sValue, setValue] = useState(
		props.value
			? new Date(Date.parse(props.value)).toLocaleDateString('en-US', {
					month: '2-digit',
					day: '2-digit',
					year: 'numeric',
			  })
			: FORMAT_DATE
	);
	const [sDays, setDays] = useState(() => ['', ...range(1, 28, 1)]);

	const refArrFocus = useRef([0, 0, 0]);
	if (props.value) {
		// if a value is pre-existing, each Select component considered "touched"
		refArrFocus.current = [1, 1, 1];
	}

	const refMonths = useRef();
	const refYears = useRef();

	/**
	 * Determines if the state value substring from @param start to @param
	 * stop exceeds the maximum element of @param range.
	 * @param {number} start Beginning index of state value substring to be checked.
	 * @param {number} stop Terminating index, exclusive, of state value substring to be checked.
	 * @param {number[]} range Array of possible elements assumed to be ordered incrementally.
	 * @returns {string} Nullable string to be used to update state value in case of overflow.
	 * @example
	 * // returns 'DD' when state value equals '01/31/YYYY', February has a maximum of 29 days.
	 * checkOverflow(3, 5, range(1, datePair(2, (isLeapYear()) ? 29 : 28), 1));
	 * // returns null when state value equals '03/15/YYYY', overflow did not occur.
	 * checkOverflow(3, 5, range(1, 31, 1));
	 */
	const checkOverflow = (start, stop, range) => {
		return Number(sValue.substring(start, stop)) > range[range.length - 1]
			? FORMAT_DATE.substring(start, stop)
			: null;
	};

	/**
	 * Filters @type {number} upper, @type {number} lower bound prop for non-numerical values and converts to @type {number}.
	 * Conditionally manages non-numerical values and converts to a numerical representation.
	 * @param {string} input Value assigned to upper, lower bound prop.
	 * @param {number} base Value to be returned if param input is null.
	 * @returns {number} Nullable numerical representation of upper, lower bound prop.
	 * @example
	 * // returns 1996
	 * filterBoundProps('1996')
	 * // returns 2019
	 * filterBoundProps('now') // 10/03/2019
	 * // returns 2024
	 * filterBoundProps('++5') // 10/03/2019
	 * // returns null
	 * filterBoundProps('today')
	 */
	const filterBoundProps = (input, base) => {
		const KEY_NOW = 'now';
		const REGEX_YEAR = /[0-9]{4}/g;
		const STRING_ADDITION = '++';
		const STRING_SUBSTRACTION = '--';
		const yearNow = new Date().getFullYear();
		let bound = Number(input);
		if (!input) {
			return base;
		}
		if (isNaN(Number(input))) {
			if (input.includes(STRING_ADDITION)) {
				input = input.replace(STRING_ADDITION, '').trim();
				if (isNaN(input)) {
					return base;
				}
				bound = yearNow + Number(input);
			} else if (input.includes(STRING_SUBSTRACTION)) {
				input = input.replace(STRING_SUBSTRACTION, '').trim();
				if (isNaN(input)) {
					return base;
				}
				bound = yearNow - Number(input);
			} else if (input === KEY_NOW) {
				bound = yearNow;
			} else {
				return base;
			}
		}
		return bound.toString().match(REGEX_YEAR) && bound > YEAR_START
			? bound
			: base;
	};

	/**
	 * Records when each Select component has received and subsequently lost focus using local ref refArrFocus. Assists in determining when
	 * validation should begin.
	 * Calls parentOnChangeHandler to address conditions where a Select component has received and lost focus but whose value was not changed.
	 * @param {Number} index Index of refArrFocus array to update. Corresponds to a Select component.
	 * @example
	 * // Updates [0,0,0] to [0,1,0]
	 * onBlurHandler(1)
	 * // When refArrFocus equals [1,1,0]: updates [1,1,0] to [1,1,1]; will trigger props.onChange() in parentOnChangeHandler()
	 * onBlurHandler(2)
	 */
	const onBlurHandler = index => {
		refArrFocus.current[index] = 1;
		parentOnChangeHandler(sValue);
	};

	/**
	 * Performs string concatenation to conditionally update each field of state value string.
	 * Performs manual call of parent onChangeHandler to propagate updated state value up hierarchy.
	 * @param {string} month Nullable month substring.
	 * @param {string} day Nullable day substring.
	 * @param {string} year Nullable year substring.
	 * @example
	 * // Updates 'MM' of 'MM/DD/YYYY' to '01/DD/YYYY'
	 * onChangeHandler('01', null, null);
	 * // Updates 'MM' and 'DD' of 'MM/DD/YYYY' to '01/01/YYYY'
	 * onChangeHandler('01', '01', null);
	 */
	const onChangeHandler = (month, day, year) => {
		const valueCurrent = sValue;
		const value =
			(month ? month : valueCurrent.substring(0, 2)) +
			'/' +
			(day ? day : valueCurrent.substring(3, 5)) +
			'/' +
			(year ? year : valueCurrent.substring(6));
		setValue(value);
		parentOnChangeHandler(value);
	};

	/**
	 * Conditionally updates the available days based on @param input.
	 * @param {string} input The selected month.
	 * @returns {{month: string, day: string}} pair of month, day values given inputted change.
	 * @example
	 * // Will update the available days to 31.
	 * onMonthHandler(1);
	 */
	const onMonthHandler = input => {
		let month = input;
		let numMonth = parseInt(input);
		let rangeDay = [];
		let day = null;
		if (numMonth) {
			const obj = monthDayPairs.find(obj => obj.month === numMonth);
			if (obj) {
				rangeDay = range(1, obj.day, 1);
			} else {
				rangeDay = range(1, 31, 1);
			}
			setDays(['', ...rangeDay]);
		} else {
			month = FORMAT_DATE.split('/')[0];
		}
		day = checkOverflow(3, 5, rangeDay);
		if (day) {
			// If day Select component selected value needs to reset
			setValue(
				value =>
					value.split('/')[0] +
					FORMAT_DATE.split('/')[1] +
					value.split('/')[2]
			);
		}
		return datePair(month, day);
	};

	/**
	 * Creates a custom event object to pass to parent onChangeHandler. Parent handler only called when all Select components have received and lost focus.
	 * @param {string} value The value of this [DateSelect] component to pass to parent onChangeHandler.
	 */
	const parentOnChangeHandler = value => {
		const event = {
			target: {
				checked: null,
				id: props.key,
				name: props.name,
				options: null,
				selectedIndex: null,
				value: value,
			},
		};
		if (refArrFocus.current.every(el => el === 1)) {
			props.onChange(event, false);
		}
	};

	// defaults to (current year) + 5
	const yearEnd = filterBoundProps(
		props.extras && props.extras.yearEnd ? props.extras.yearEnd : null,
		new Date().getFullYear() + 5
	);
	const yearStart = filterBoundProps(
		props.extras && props.extras.yearStart ? props.extras.yearStart : null,
		YEAR_START
	);

	refMonths.current = ['', ...range(1, 12, 1)];
	refYears.current = ['', ...range(yearStart, yearEnd, 1)];

	/**
	 * Initial render lifecycle hook.
	 * Conditionally updates the available days as well as the year value given sValue and data configurations.
	 *
	 * refYears is the only data-configurable property. Hook checks if an existing year value
	 * is still valid in the context of the data-configured refYears. If not valid, sValue is updated accordingly.
	 */
	useEffect(() => {
		onMonthHandler(sValue.substring(0, 2));
		if (!isNaN(sValue.substring(6, 10))) {
			if (!refYears.current.includes(sValue.substring(6, 10))) {
				onChangeHandler(null, null, FORMAT_DATE.substring(6, 10));
				setValue(
					value => value.substring(0, 6) + FORMAT_DATE.split('/')[2]
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	let classesSelect = classnames({ [`${classes[CSS_CLASSES.DATE]}`]: true });

	const baseElementID = () => {
		if (props.appId && props.componentId) {
			return `app-${props.appId}__component-${props.componentId}`;
		} else {
			return `date-select-${formatForID(props.name)}`;
		}
	};

	return (
		<StyleAuxiliary
			{...props}
			classes={classes[CSS_CLASSES.CONTAINER_DATE]}
		>
			<FormControl>
				<InputLabel
					id={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[0]
					)}__label`}
				>
					{FORMAT_DATE.split('/')[0]}
				</InputLabel>
				<Select
					id={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[0]
					)}`}
					className={classesSelect}
					label={FORMAT_DATE.split('/')[0]}
					labelId={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[0]
					)}__label`}
					onBlur={() => onBlurHandler(0)}
					onChange={e => {
						const objDate = onMonthHandler(
							e.target.value
								? e.target.value
								: FORMAT_DATE.split('/')[0],
							null,
							null
						);
						onChangeHandler(objDate.month, objDate.day, null);
					}}
					value={
						isNaN(sValue.split('/')[0]) ? '' : sValue.split('/')[0]
					}
				>
					{refMonths.current.map(month => (
						<MenuItem
							sx={{ color: month ? 'inherit' : 'transparent' }}
							key={month}
							value={month}
						>
							{month ? month : '--'}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<FormControl>
				<InputLabel
					id={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[1]
					)}__label`}
				>
					{FORMAT_DATE.split('/')[1]}
				</InputLabel>
				<Select
					id={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[1]
					)}`}
					className={classesSelect}
					label={FORMAT_DATE.split('/')[1]}
					labelId={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[1]
					)}__label`}
					onBlur={() => onBlurHandler(1)}
					onChange={e =>
						onChangeHandler(
							null,
							e.target?.value
								? e.target.value
								: FORMAT_DATE.split('/')[1],
							null
						)
					}
					value={
						isNaN(sValue.split('/')[1]) ? '' : sValue.split('/')[1]
					}
				>
					{sDays.map(day => (
						<MenuItem
							sx={{ color: day ? 'inherit' : 'transparent' }}
							key={day}
							value={day}
						>
							{day ? day : '--'}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<FormControl>
				<InputLabel
					id={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[2]
					)}__label`}
				>
					{FORMAT_DATE.split('/')[2]}
				</InputLabel>
				<Select
					id={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[2]
					)}`}
					className={classesSelect}
					label={FORMAT_DATE.split('/')[2]}
					labelId={`${baseElementID()}__unit-${formatForID(
						FORMAT_DATE.split('/')[2]
					)}__label`}
					onBlur={() => onBlurHandler(2)}
					onChange={e =>
						onChangeHandler(
							null,
							null,
							e.target?.value
								? e.target.value
								: FORMAT_DATE.split('/')[2]
						)
					}
					value={
						isNaN(sValue.split('/')[2]) ? '' : sValue.split('/')[2]
					}
				>
					{refYears.current.map(year => (
						<MenuItem
							sx={{ color: year ? 'inherit' : 'transparent' }}
							key={year}
							value={year}
						>
							{year ? year : '--'}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</StyleAuxiliary>
	);
};

DateSelect.propTypes = {
	yearEnd: PropTypes.string,
	yearStart: PropTypes.string,
};

/**
 * Creats an array containing @param step incrementals from @param start to @param stop, inclusive.
 * @param {number} start Lower bound of array range.
 * @param {number} stop Upper bound of array range.
 * @param {number} step Increment coefficient.
 * @return {(string\|number)[]} Array of range.
 * @example
 * // Creates array equal to [00, 02, 04, 06, 08, 10]
 * range(0, 10, 2);
 */
const range = (start, stop, step) =>
	Array.from({ length: (stop - start) / step + 1 }, (_, i) => {
		let el = (start + i * step).toString();
		if (el.length < 2) {
			el = '0' + el;
		}
		return el;
	});

/**
 * Determines if current year is a valid leap year.
 * @return {boolean} Boolean value.
 * @example
 * // Returns false
 * isLeapYear(1000);
 * // Returns true
 * isLeapYear(2000);
 */
const isLeapYear = () => {
	const year = new Date().getFullYear();
	if (year % 4 === 0) {
		if (year % 100 === 0 && year % 400 !== 0) {
			return false;
		}
		return true;
	}
	return false;
};

/**
 * Creats object of type {month: @param month, day: @param day}.
 * @param {number} month Value of property 'month'
 * @param {number} day Value of property 'day'
 * @return {{month: string, day: string}} Object instance.
 */
const datePair = (month, day) => {
	return { month: month, day: day };
};

/**
 * Creates an array of objects of type {month: @type {int}, day: @type {int}} to indicate months
 * departing from a 31-day standard.
 */
const monthDayPairs = [
	datePair(2, isLeapYear() ? 29 : 28),
	datePair(4, 30),
	datePair(6, 30),
	datePair(9, 30),
	datePair(11, 30),
];

export default DateSelect;
